import {
  Button,
  DataTable,
  ErrorState,
  LoadingState,
  Modal,
  NotAvailable,
  SelectableChip,
  Stat,
  StatusPill,
  type DataTableColumn,
} from '../../components';
import { formatCents, formatCount } from '../../lib/format/money';
import { formatDate } from '../../lib/format/dates';
import type { OrderSummary, ProfileDetail } from '../profiles/api';
import { AdminNotesPanel } from '../profiles/AdminNotesPanel';
import { DeactivateProfileButton } from '../profiles/DeactivateProfileButton';
import { DemoProfileButton } from '../profiles/DemoProfileButton';
import { ReviewsReceived } from '../profiles/ReviewsReceived';
import { UserMessagePanel } from '../profiles/UserMessagePanel';
import { useSession } from '../../lib/auth/useSession';
import { useDetailLinks } from '../orders/DetailDialogs';
import { useProfileDetail } from '../profiles/queries';
import {
  ACCOUNT_STATUS_TONE,
  ProfileHeader,
  SEGMENT_LABEL,
  SEGMENT_TONE,
  SectionLabel,
  humanize,
  orderStatusTone,
  segmentOf,
} from '../profiles/people';

export const EATER_TABS = ['profile', 'orders', 'points', 'notes', 'message'] as const;
export type EaterTab = (typeof EATER_TABS)[number];
const TAB_LABEL: Record<EaterTab, string> = {
  profile: 'Profile',
  orders: 'Orders',
  points: 'Points',
  notes: 'Notes',
  message: 'Message',
};

const ORDER_COLUMNS: DataTableColumn<OrderSummary>[] = [
  {
    id: 'order',
    header: 'Order',
    width: '104px',
    cell: (r) => <span className="font-mono text-xs">{r.orderNumber}</span>,
  },
  {
    id: 'date',
    header: 'Date',
    width: '1fr',
    cell: (r) => <span className="text-muted">{formatDate(r.createdAt)}</span>,
  },
  {
    id: 'status',
    header: 'Status',
    // Fixed track sized to the widest status pill ("Ready to pickup").
    width: '136px',
    cell: (r) => <StatusPill tone={orderStatusTone(r.status)}>{humanize(r.status)}</StatusPill>,
  },
  { id: 'qty', header: 'Qty', width: '52px', align: 'right', numeric: true, cell: (r) => r.quantity },
  { id: 'total', header: 'Total', width: '84px', align: 'right', numeric: true, cell: (r) => formatCents(r.totalPaid) },
];

/**
 * Eater profile dialog (kit `EaterProfileModal`, lg). `?eater=<id>`.
 * AOV is not on the directory row; it is computed here from the order
 * history — completed orders among the 50 most recent — and labelled so.
 */
export function EaterDialog({
  id,
  tab,
  onTab,
  onClose,
}: {
  id: string;
  tab: EaterTab;
  onTab: (tab: EaterTab) => void;
  onClose: () => void;
}) {
  const detail = useProfileDetail(id);
  const { can } = useSession();
  const d = detail.data;
  // Message is hidden, not disabled, without user.message; a ?eaterTab=message
  // link without it opens Profile.
  const tabs: readonly EaterTab[] = can('user.message') ? EATER_TABS : EATER_TABS.filter((t) => t !== 'message');
  const shown = tabs.includes(tab) ? tab : 'profile';

  return (
    <Modal isOpen onClose={onClose} size="xl">
      {detail.isPending ? (
        <LoadingState label="Loading eater…" />
      ) : detail.isError || !d ? (
        <ErrorState error={detail.error} onRetry={() => void detail.refetch()} />
      ) : (
        <EaterBody detail={d} tabs={tabs} tab={shown} onTab={onTab} />
      )}
      <div className="mt-[18px] flex justify-end gap-2 border-t border-[rgba(143,68,32,.1)] pt-4">
        <Button variant="ghost-dark" size="sm" onClick={onClose}>
          Close
        </Button>
        {d && <DeactivateProfileButton profileId={id} status={d.profile.status} noun="eater" onDone={onClose} />}
        {d && shown === 'notes' && <DemoProfileButton profileId={id} demo={!!d.profile.demo} noun="eater" />}
      </div>
    </Modal>
  );
}

function EaterBody({
  detail,
  tabs,
  tab,
  onTab,
}: {
  detail: ProfileDetail;
  tabs: readonly EaterTab[];
  tab: EaterTab;
  onTab: (tab: EaterTab) => void;
}) {
  const { profile } = detail;
  const segment = segmentOf(profile.orderCount);

  return (
    <>
      <ProfileHeader
        name={profile.displayName}
        avatarUrl={profile.avatarUrl}
        detail={
          <>
            <div>Joined {formatDate(profile.createdAt)}</div>
            <div className="mt-0.5 font-mono text-xs">{profile.id}</div>
          </>
        }
        pills={
          <>
            <StatusPill tone={SEGMENT_TONE[segment]} dot>
              {SEGMENT_LABEL[segment]}
            </StatusPill>
            {profile.status !== 'active' && (
              <StatusPill tone={ACCOUNT_STATUS_TONE[profile.status] ?? 'neutral'}>
                Account · {humanize(profile.status)}
              </StatusPill>
            )}
          </>
        }
      />

      <div className="mb-[18px] flex flex-wrap gap-[7px]" role="tablist" aria-label="Eater profile sections">
        {tabs.map((t) => (
          <SelectableChip key={t} role="tab" aria-selected={tab === t} active={tab === t} onClick={() => onTab(t)}>
            {TAB_LABEL[t]}
          </SelectableChip>
        ))}
      </div>

      <div role="tabpanel" aria-label={TAB_LABEL[tab]}>
        {tab === 'profile' && <ProfileTab detail={detail} />}
        {tab === 'orders' && <OrdersTab detail={detail} />}
        {tab === 'points' && (
          <NotAvailable title="Points" reason="Points are mocked in phase 1 and arrive with the Points screen." />
        )}
        {tab === 'notes' && <AdminNotesPanel profileId={profile.id} notes={detail.adminNotes} noun="eater" />}
        {tab === 'message' && <UserMessagePanel profile={profile} noun="eater" />}
      </div>
    </>
  );
}

function ProfileTab({ detail }: { detail: ProfileDetail }) {
  const { profile, orderHistory } = detail;
  const completed = orderHistory.filter((o) => o.status === 'completed');
  const aov = completed.length ? completed.reduce((sum, o) => sum + o.totalPaid, 0) / completed.length : null;

  return (
    <>
      <div className="mb-[18px] grid grid-cols-3 gap-2.5">
        <Stat label="Orders" value={formatCount(profile.orderCount)} hint="lifetime" />
        <Stat label="Completed" value={formatCount(completed.length)} hint="of the last 50" />
        <Stat
          label="Avg. order value"
          value={aov === null ? '—' : formatCents(Math.round(aov))}
          hint="completed, last 50"
        />
      </div>

      <div className="grid grid-cols-1 gap-3.5 text-sm desk:grid-cols-2">
        <div>
          <SectionLabel>Email</SectionLabel>
          {profile.email || <span className="text-muted">—</span>}
        </div>
        <div>
          <SectionLabel>Phone</SectionLabel>
          {profile.phoneE164 || <span className="text-muted">—</span>}
        </div>
      </div>

      <ReviewsReceived profileId={profile.id} as="eater" />
    </>
  );
}

function OrdersTab({ detail }: { detail: ProfileDetail }) {
  const { orderHistory } = detail;
  const { openOrder } = useDetailLinks();
  return (
    <>
      <SectionLabel>Order history</SectionLabel>
      {orderHistory.length === 0 ? (
        <div className="text-[13px] text-muted">No orders yet.</div>
      ) : (
        <DataTable
          label="Order history"
          compact
          minWidth={0}
          columns={ORDER_COLUMNS}
          rows={orderHistory}
          getRowId={(r) => r.id}
          onRowClick={(r) => openOrder(r.id)}
        />
      )}
    </>
  );
}
